import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';

import { BorderedSection, Button, Spinner } from '../../components';
import TitleBar from '../../components/TitleBar';
import TableV3 from '../../components/generic/Table/TableV3';

import { FIND_ALL_BCP_CORPORATES, FIND_ALL_LOCATIONS } from './queries';
import { tableSortingMethods, getStatusEmoji } from '../../utils';
import QueryErrorBoundary from '../../components/error-handling';
import {
  BcpCorporate,
  FindAllBcpCorporates,
  FindAllLocations,
  Location,
} from '../../interfaces/bcp';
import { formatDate } from '../../utils/formatDate';
import { ExportColumn } from '../../interfaces/ExportColumn';
import { getFilteredTags } from '../../utils/getFilteredTags';
import { useUserContext } from '../../context/PermissionsContext';
import TitleUpdater from '../../components/common/TitleUpdater';

function BCPSelectionPage() {
  const navigate = useNavigate();

  const { isLocation, selectedTags } = useUserContext();

  const {
    data: corporate,
    loading: corporateLoading,
    error: corporateError,
  } = useQuery<FindAllBcpCorporates>(FIND_ALL_BCP_CORPORATES);

  const corporateData = corporate?.findAllBcpCorporates || [];

  const {
    data: locations,
    loading: locationsLoading,
    error: locationsError,
  } = useQuery<FindAllLocations>(FIND_ALL_LOCATIONS);

  const locationsData = locations?.findAllLocations || [];

  const filteredLocations = getFilteredTags({
    data: locationsData,
    selectedTags,
    condition: isLocation,
    lookUpKey: 'uuid',
  });

  const CORPORATE_BCP_SLUG = corporateData?.length
    ? `/bcp-landing/corporate/${corporateData[0].uuid}`
    : '';

  const MONTHS_TO_WEEKS_MULTIPLIER = 4.34524;

  const columns: ColumnsType<Location> = [
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column font-bold text-primary-200">
          {text}
        </span>
      ),
    },
    {
      title: 'Primary Strategy Status',
      dataIndex: 'primaryStrategyStatus',
      key: 'primaryStrategyStatus',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.string(
          a?.primaryStrategyStatus ?? '',
          b?.primaryStrategyStatus ?? '',
        ),
      render: (text: string) => (
        <span className="custom-table--column">
          {text ? (
            `${getStatusEmoji(text)} ${text}`
          ) : (
            <span className="placeholder-text">No available status</span>
          )}
        </span>
      ),
    },
    {
      title: () => (
        <span>
          Primary Strategy Age <br /> (weeks)
        </span>
      ),
      dataIndex: 'primaryStrategyAge',
      key: 'primaryStrategyAge',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.primaryStrategyAge, b.primaryStrategyAge),
      render: (text: number) => (
        <span className="custom-table--column">
          {(text * MONTHS_TO_WEEKS_MULTIPLIER).toFixed(0)}
        </span>
      ),
    },
    {
      title: 'Total Location Strategies',
      dataIndex: 'totalLocationStrategies',
      key: 'totalLocationStrategies',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.totalLocationStrategies,
          b.totalLocationStrategies,
        ),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Admin Items Last Updated',
      dataIndex: 'lastBcpUpdate',
      key: 'lastBcpUpdate',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.date(a.lastBcpUpdate, b.lastBcpUpdate),
      render: (text: string, { bcpStatus }) => {
        return (
          <span className="custom-table--column">
            {bcpStatus ? (
              text?.slice(0, 10)
            ) : (
              <span className="placeholder-text">Never</span>
            )}
          </span>
        );
      },
    },
    {
      title: 'Process Area Strategies',
      dataIndex: 'processAreaStrategies',
      key: 'processAreaStrategies',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.processAreaStrategies,
          b.processAreaStrategies,
        ),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Process Strategies',
      dataIndex: 'processesStrategies',
      key: 'processesStrategies',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.processesStrategies,
          b.processesStrategies,
        ),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
  ];

  const extendActions = (record: Location) => {
    return (
      <Button
        type="primary-rounded"
        className="text-sm font-semibold min-w-[125px]"
        onClick={() => navigate(`/bcp-landing/location/${record.uuid}`)}
      >
        Review Location
      </Button>
    );
  };

  const baseColumns: ExportColumn[] = [
    {
      key: 'locationName',
      header: 'Location',
      type: 'text',
      width: 30,
    },
    {
      key: 'primaryStrategyStatus',
      header: 'Primary Strategy Status',
      type: 'text',
      placeholder: 'Not Started',
      width: 30,
    },
    {
      key: 'primaryStrategyAge',
      header: 'Primary Strategy Age',
      type: 'text',
      width: 30,
    },
    {
      key: 'totalLocationStrategies',
      header: 'Total Location Strategies',
      type: 'text',
      width: 30,
    },
    {
      key: 'lastBcpUpdate',
      header: 'Admin Items Last Updated',
      type: 'date',
      width: 30,
    },
    {
      key: 'processAreaStrategies',
      header: 'Process Area Strategies',
      type: 'text',
      width: 30,
    },
    {
      key: 'processesStrategies',
      header: 'Process Strategies',
      type: 'text',
      width: 30,
    },
  ];

  return (
    <>
      <TitleUpdater title={`BCP Hub`} />
      <TitleBar title="BCP Hub" subtitle="Overview" />
      <div className="w-full flex flex-col gap-6 items-center mt-4">
        <QueryErrorBoundary error={corporateError}>
          <BorderedSection title="" headingClassName="hidden">
            <CorporateBCPTable
              data={corporateData}
              loading={corporateLoading}
              onClick={() => navigate(CORPORATE_BCP_SLUG)}
            />
          </BorderedSection>
        </QueryErrorBoundary>
        <QueryErrorBoundary error={locationsError}>
          <TableV3
            title="Location BCPs"
            columns={columns}
            data={filteredLocations}
            loading={locationsLoading}
            extendActions={extendActions}
            exportExcelColumns={baseColumns}
            customStyles="custom-column-PA--heading"
          />
        </QueryErrorBoundary>
      </div>
    </>
  );
}

const CorporateBCPTable = ({
  data,
  loading,
  onClick,
}: {
  data: BcpCorporate[];
  loading: boolean;
  onClick: () => void;
}) => {
  const { lastBcpUpdate } = data[0] || {};

  if (loading) return <Spinner />;

  return (
    <div className="flex items-center justify-between">
      <div className="font-medium">Corporate BCP Items</div>
      <div>
        <span className="font-medium">Last Update: </span>{' '}
        {lastBcpUpdate ? formatDate(lastBcpUpdate) : 'NEVER'}
      </div>
      <Button
        type="primary"
        className="text-sm  min-w-[188px]"
        onClick={onClick}
      >
        Review / Update
      </Button>
    </div>
  );
};

export default BCPSelectionPage;
